import {
	FC, ReactNode, useEffect, useMemo,
} from 'react';
import {
	RedditAdvertiserListQueryItems, SearchUser, User, UserGroup,
} from 'wb-reddit/graphql/generated';
import redditLogo from 'wb-reddit/assets/reddit-logo.png';
import spotifyLogo from 'wb-spotify/assets/spotify-logo.png';
import {
	AdvertiserFallbackIcon,
	AppEnum,
	Avatar,
	AvatarSize,
	TeamsFallbackIcon,
	UserFallbackIcon,
	useAvatarColorStore,
} from '@aleph/shared-components';
import { getAvatarFallbackSize } from 'wb-reddit/app/routes/reports/utils';
import { AuthGraphAdvertiserListQueryItems } from 'wb-spotify/graphql/authgraph/generated';

export type RedditImageEntity = Partial<
Pick<UserGroup, '__typename' | 'userGroupProfilePictureUrl' | 'userGroupId'> & {
	users: unknown[] | undefined | null,
	userGroupProfilePicture?: string | null
} |
Pick<User, '__typename' | 'userProfilePictureUrl' | 'userId'> & { userProfilePicture?: string | null } |
Pick<SearchUser, '__typename' | 'userProfilePictureUrl' | 'userId'> & { userProfilePicture?: string | null } |
Pick<RedditAdvertiserListQueryItems, '__typename' | 'agencyAccountProfilePicture' | 'agencyAccountId'> |
{ __typename?: 'Client' }
> & { profilePicture?: string | null, profilePictureUrl?: string | null };

function getRandomIconColor() {
	const colorsToPickFrom = [
		'hsl(var(--orange-400))',
		'hsl(var(--green-500))',
		'hsl(var(--turquoise-500))',
		'hsl(var(--cyan-500))',
		'hsl(var(--violet-500))',
		'hsl(var(--purple-500))',
		'hsl(var(--rose-500))',
	];

	const max = colorsToPickFrom.length - 1;

	return colorsToPickFrom[Math.floor(Math.random() * (max + 1))];
}

function getEntityId(entity: RedditImageEntity | AuthGraphAdvertiserListQueryItems) {
	switch (entity?.__typename) {
		case 'SearchUser':
		case 'User':
			return `User${entity.userId}`;
		case 'AgencyAccount':
			return `${entity.__typename}${entity.agencyAccountId}`;
		case 'UserGroup':
			return `${entity.__typename}${entity.userGroupId}`;
	}

	return '';
}

export const fallBackUserImage = () => {
	switch (import.meta.env.VITE_PACKAGE_APP) {
		case AppEnum.SPOTIFY:
			return spotifyLogo;
		case AppEnum.REDDIT:
		default:
			return redditLogo;
	}
};

const RedditEntityImage: FC<{
	entity: RedditImageEntity | AuthGraphAdvertiserListQueryItems
	size?: AvatarSize
}> = ({ entity, size = AvatarSize.Small }) => {
	let profilePicture: ReactNode = (entity as RedditImageEntity)?.profilePicture;

	const entityId = getEntityId(entity);

	const fill = useAvatarColorStore((state => state.entityIdColors[entityId]));
	const addEntityColor = useAvatarColorStore((state => state.addEntityColor));

	useEffect(() => {
		const newColor = getRandomIconColor();

		addEntityColor({ [entityId]: newColor });
	}, [entityId]);

	const avatarIconSize = useMemo(() => getAvatarFallbackSize(size), []);

	if (entity?.__typename === 'UserGroup') {
		profilePicture = entity?.userGroupProfilePictureUrl
			?? entity?.userGroupProfilePicture
			?? entity?.profilePicture
			?? entity?.profilePictureUrl
			?? (
				<TeamsFallbackIcon
					fill={fill}
					width={avatarIconSize}
					height={avatarIconSize}
				/>
			);
	}

	if (entity?.__typename === 'User' || entity?.__typename === 'SearchUser') {
		profilePicture = entity?.userProfilePictureUrl
			?? entity?.userProfilePicture
			?? entity?.profilePicture
			?? entity?.profilePictureUrl
			?? (
				<UserFallbackIcon
					fill={fill}
					width={avatarIconSize}
					height={avatarIconSize}
				/>
			);
	}

	if (entity?.__typename === 'AgencyAccount') {
		profilePicture = entity?.agencyAccountProfilePicture
			?? entity?.profilePicture
			?? entity?.profilePictureUrl
			?? (
				<AdvertiserFallbackIcon
					fill={fill}
					width={avatarIconSize}
					height={avatarIconSize}
				/>
			);
	}

	profilePicture = profilePicture ?? fallBackUserImage();

	const count = entity?.__typename === 'UserGroup' ? entity?.users?.length : undefined;

	return (
		<Avatar
			avatarSource={profilePicture}
			count={count}
			size={size}
		/>
	);
};

export default RedditEntityImage;
